import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useDeliveryAddress } from '../context/DeliveryAddressContext';
import { API_URL } from '../config/api';
import { getFullImageUrl } from '../utils/imageUtils';
import { isCurrentlyOpen, getTodayOpeningHours } from '../utils/openingHours';
import ItemDetailModal from '../components/ItemDetailModal';
import SEO from '../components/SEO';
import './StoreDetail.css';

const StoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { user, updateUser } = useAuth();
  const { currentAddress, addressVersion } = useDeliveryAddress();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const lastFetchedCoordinates = useRef(null);

  const fetchStore = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      let url = `${API_URL}/stores/${id}`;
      let coordinatesKey = null;
      // Add user location if available
      if (currentAddress?.coordinates?.lat && currentAddress?.coordinates?.lng) {
        const lat = parseFloat(currentAddress.coordinates.lat);
        const lng = parseFloat(currentAddress.coordinates.lng);
        if (!isNaN(lat) && !isNaN(lng)) {
          url += `?lat=${lat}&lng=${lng}`;
          coordinatesKey = `${lat},${lng}`;
        }
      }
      const response = await axios.get(url);
      setStore(response.data);
      lastFetchedCoordinates.current = coordinatesKey;
    } catch (error) {
      console.error('Error fetching store:', error);
    } finally {
      setLoading(false);
    }
  }, [id, currentAddress, addressVersion]);

  useEffect(() => {
    fetchStore();
  }, [fetchStore]);

  // Refetch when currentAddress becomes available (e.g., loaded from localStorage)
  // This ensures distance is calculated even if address loads after initial fetch
  useEffect(() => {
    if (id && currentAddress?.coordinates?.lat && currentAddress?.coordinates?.lng) {
      const lat = parseFloat(currentAddress.coordinates.lat);
      const lng = parseFloat(currentAddress.coordinates.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        const coordinatesKey = `${lat},${lng}`;
        // Refetch if we haven't fetched with these coordinates yet, or if store doesn't have distance
        if (lastFetchedCoordinates.current !== coordinatesKey || 
            !store || 
            store.distance === undefined || 
            store.distance === null) {
          fetchStore();
        }
      }
    }
  }, [id, currentAddress?.coordinates?.lat, currentAddress?.coordinates?.lng, addressVersion, fetchStore]);

  useEffect(() => {
    if (user && store) {
      const favoriteIds = user.favoriteStores?.map(s => s.toString ? s.toString() : s) || [];
      setIsFavorite(favoriteIds.includes(store._id));
    } else {
      setIsFavorite(false);
    }
  }, [user, store]);

  const getProductCategories = () => {
    if (!store?.products) return [];
    const categories = [...new Set(store.products.map(product => product.category))];
    return ['All', ...categories];
  };

  const getFilteredProducts = () => {
    if (!store?.products) return [];
    if (selectedCategory === 'All') return store.products;
    return store.products.filter(product => product.category === selectedCategory);
  };

  const isOpen = () => isCurrentlyOpen(store);

  const handleToggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (isFavorite) {
        await axios.delete(`${API_URL}/users/favorite-stores/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        if (user.favoriteStores) {
          user.favoriteStores = user.favoriteStores.filter(s => {
            const storeId = s.toString ? s.toString() : s;
            return storeId !== id;
          });
          updateUser(user);
        }
      } else {
        await axios.post(`${API_URL}/users/favorite-stores/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        if (!user.favoriteStores) {
          user.favoriteStores = [];
        }
        user.favoriteStores.push(id);
        updateUser(user);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const renderStars = (rating) => {
    if (rating == null || rating <= 0) return null;
    const stars = [];
    const fullStars = Math.min(5, Math.max(0, Math.round(rating)));
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={i <= fullStars ? 'fas fa-star' : 'far fa-star'}></i>
      );
    }
    return stars;
  };

  if (loading) {
    return <div className="store-detail-container"><div className="container">Loading...</div></div>;
  }

  if (!store) {
    return <div className="store-detail-container"><div className="container">Store not found</div></div>;
  }

  const productCategories = getProductCategories();
  const filteredProducts = getFilteredProducts();

  return (
    <div className="store-detail-container">
      <SEO
        title={store.name}
        description={store.description?.trim() || `${store.name} – tilaa päivittäistavarat kotiin Pussikuskin kautta.`}
        image={store.banners?.[0] ? getFullImageUrl(store.banners[0]) : undefined}
        path={`/store/${store._id}`}
      />
      <div className="store-detail">
        {/* Banner Background */}
        {store.banners && store.banners.length > 0 && (
          <div 
            className="store-banner-background"
            style={{ backgroundImage: `url(${getFullImageUrl(store.banners[0])})` }}
          >
            <button 
              className={`banner-favorite-btn ${isFavorite ? 'active' : ''}`}
              onClick={handleToggleFavorite}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              <i className={isFavorite ? 'fas fa-heart' : 'far fa-heart'}></i>
            </button>
            <div className={`store-status-overlay ${isOpen() ? 'open' : 'closed'}`}>
              {store.closed
                ? `🔴 ${t('restaurant.orderingPausedDueToCongestion')}`
                : isOpen()
                  ? `🟢 ${t('restaurant.open')}`
                  : `🔴 ${t('restaurant.closed')}`}
            </div>
            {currentAddress?.coordinates?.lat &&
             currentAddress?.coordinates?.lng &&
             typeof store.distance === 'number' &&
             !isNaN(store.distance) &&
             store.distance > 0 && (() => {
              const maxDist = store.maxDeliveryDistance ?? 20;
              if (store.distance > maxDist) {
                return (
                  <div className="store-delivery-overlay-banner">
                    {t('restaurant.outsideDeliveryArea')}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        <div className="store-detail-header">
          <div className="store-title-row">
            <div className="store-title-left">
              <h1>{store.name}</h1>
              {store.description && <p className="description">{store.description}</p>}
              <div className="pricing-range">{store.pricingRange}</div>
            </div>
            <div className="store-title-right">
              {(store.settings?.displayStarRatings !== false && store.rating != null && store.rating > 0) && (
                <div className="rating-stars">
                  {renderStars(store.rating)}
                </div>
              )}
              <button 
                className={`favorite-btn ${isFavorite ? 'active' : ''}`}
                onClick={handleToggleFavorite}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <i className={isFavorite ? 'fas fa-heart' : 'far fa-heart'}></i>
              </button>
            </div>
          </div>
          
          <div className="store-meta-info">
            {(store.distance != null && !isNaN(Number(store.distance)) && Number(store.distance) > 0) ? (
              <span className="distance">{t('restaurant.distanceAway', { distance: Number(store.distance).toFixed(1) })}</span>
            ) : null}
            {(() => {
              const today = getTodayOpeningHours(store);
              if (today) {
                return <span className="hours">{today.openingTime} – {today.closingTime}</span>;
              }
              if (store?.openingHours) {
                return <span className="hours">{t('restaurant.closedToday') || 'Closed today'}</span>;
              }
              return null;
            })()}
          </div>

          {/* Delivery Zone Warning */}
          {           currentAddress?.coordinates?.lat && 
           currentAddress?.coordinates?.lng &&
           typeof store.distance === 'number' &&
           !isNaN(store.distance) &&
           store.distance > 0 && (
            (() => {
              const maxDist = store.maxDeliveryDistance ?? 20; // Default 20km if not set
              if (store.distance > maxDist) {
                return (
                  <div className="delivery-zone-warning">
                    <i className="fas fa-exclamation-triangle"></i>
                    <div className="warning-content">
                      <strong>{t('restaurant.notDeliveringToLocation')}</strong>
                      <p>{t('restaurant.deliveryZoneDescriptionStore', { distance: store.distance.toFixed(1), max: maxDist })}</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()
          )}

          <div className="store-divider"></div>

          <div className="delivery-info-section">
            <div className="delivery-info-item">
              <i className="fas fa-clock"></i>
              <span>{store.deliveryTime || '2-3 days'}</span>
            </div>
            <div className="delivery-info-item">
              <i className="fas fa-euro-sign"></i>
              <span>{t('restaurant.deliveryFee')}: €{store.deliveryFee ?? '0'}</span>
            </div>
            <div className="delivery-info-item">
              <i className="fas fa-shopping-cart"></i>
              <span>{t('restaurant.minimumOrder')}: €{(store.minOrderPrice ?? store.minimumOrder ?? 0).toFixed(2)}</span>
            </div>
          </div>

          {store.openingHours && (
            <div className="opening-hours-section">
              <button
                type="button"
                className="opening-hours-toggle"
                onClick={() => setShowOpeningHours((v) => !v)}
                aria-expanded={showOpeningHours}
              >
                {t('restaurant.viewOpeningHours')}
                <i className={`fas fa-chevron-${showOpeningHours ? 'up' : 'down'}`} aria-hidden="true"></i>
              </button>
              {showOpeningHours && (
                <>
                  <h3 className="opening-hours-title">{t('restaurant.openingHours')}</h3>
                  <ul className="opening-hours-list">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                      const dayHours = store.openingHours[day];
                      const isOpen = dayHours?.isOpen !== false && dayHours?.openingTime && dayHours?.closingTime;
                      return (
                        <li key={day} className="opening-hours-row">
                          <span className="opening-hours-day">{t(`restaurant.${day}`)}</span>
                          <span className="opening-hours-time">
                            {isOpen ? `${dayHours.openingTime} – ${dayHours.closingTime}` : t('restaurant.closed')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}

          {(store.phoneNumber || store.contactInformation) && (
            <div className="contact-info-section">
              <button
                type="button"
                className="opening-hours-toggle contact-info-toggle"
                onClick={() => setShowContactInfo((v) => !v)}
                aria-expanded={showContactInfo}
              >
                {t('restaurant.contactInformation')}
                <i className={`fas fa-chevron-${showContactInfo ? 'up' : 'down'}`} aria-hidden="true"></i>
              </button>
              {showContactInfo && (
                <div className="contact-info-content">
                  {store.phoneNumber && (
                    <p className="contact-phone">
                      <a href={`tel:${store.phoneNumber.replace(/\s/g, '')}`}>{store.phoneNumber}</a>
                    </p>
                  )}
                  {store.contactInformation && (
                    <p className="contact-text">{store.contactInformation}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {store.deliveryMethods && (
            <div className="delivery-methods-info">
              {store.deliveryMethods.delivery && (
                <span className="delivery-method-tag">{t('restaurant.delivery')}</span>
              )}
              {store.deliveryMethods.pickup && (
                <span className="delivery-method-tag">{t('restaurant.pickup')}</span>
              )}
              {store.deliveryMethods.eatIn && (
                <span className="delivery-method-tag">{t('restaurant.eatIn')}</span>
              )}
            </div>
          )}
        </div>

        {/* Product Categories */}
        {productCategories.length > 1 && (
          <div className="product-categories">
            {productCategories.map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Products */}
        <div className="products-section">
          <h2>Products</h2>
          {filteredProducts.length === 0 ? (
            <p>No products in this category</p>
          ) : (
            <div className="products">
              {filteredProducts.map((product) => (
                <div 
                  key={product._id} 
                  className="product-card"
                  onClick={() => {
                    setSelectedItem(product);
                    setShowItemModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="product-content">
                    <div className="product-left">
                      {product.image ? (
                        <div className="product-image-container">
                          <img src={getFullImageUrl(product.image)} alt={product.name} className="product-image" loading="lazy" decoding="async" />
                        </div>
                      ) : (
                        <div className="product-image-container">
                          <div className="product-image-placeholder">
                            <i className="fas fa-image"></i>
                          </div>
                        </div>
                      )}
                      <div className="product-price-below-image">
                        <span className="product-price">€{product.price != null ? Number(product.price).toFixed(2) : '0.00'}</span>
                        {store.settings?.trackStock && (product.stockQty == null || product.stockQty <= 0) && (
                          <span className="sold-out-badge">{t('restaurant.soldOut') || 'Sold out'}</span>
                        )}
                      </div>
                    </div>
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      {product.description && <p className="product-description">{product.description}</p>}
                      {product.category && <span className="product-category">{product.category}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showItemModal && selectedItem && (
        <ItemDetailModal
          isOpen={showItemModal}
          onClose={() => {
            setShowItemModal(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          restaurantId={store._id}
          restaurantName={store.name}
          isProduct={true}
          orderingDisabled={store.closed || (!!store.settings?.trackStock && (selectedItem?.stockQty == null || selectedItem?.stockQty <= 0))}
          soldOut={!!store.settings?.trackStock && (selectedItem?.stockQty == null || selectedItem?.stockQty <= 0)}
          onSelectRelatedItem={(product) => setSelectedItem(product)}
        />
      )}
    </div>
  );
};

export default StoreDetail;
